/**
 * Sortable rule row for Flow Optimizer Tag Rules (ranking-aware summary).
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { GripVertical, Pencil, Sparkles, Trash2 } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { FlowTagRule } from '@trafficpulse/shared';

import { Button, Checkbox, Switch } from '@/components/ui';
import { cn } from '@/lib/utils';

import type { TagRuleScope } from '@/features/tag-rule-scope';

import { formatFlowCondition, getFlowRuleDateLabel } from './flow-tag-format';
import { FlowScopedConditionLine } from './FlowScopedConditionLine';

interface FlowSortableRuleRowProps {
  rule: FlowTagRule;
  onEdit: () => void;
  onDelete: () => void;
  onToggleEnabled: () => void;
  isReorderPending: boolean;
  selected?: boolean;
  onSelectChange?: (checked: boolean) => void;
  /** Country/campaign in view; values render as that scope resolves them. */
  scope?: TagRuleScope;
}

export const FlowSortableRuleRow: FC<FlowSortableRuleRowProps> = ({
  rule,
  onEdit,
  onDelete,
  onToggleEnabled,
  isReorderPending,
  selected,
  onSelectChange,
  scope,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: rule.id,
  });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const noStats = rule.appliesTo === 'no-stats';
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center justify-between gap-4 px-6 py-4 transition-colors hover:bg-muted/30',
        !rule.enabled && 'opacity-60',
        isDragging && 'z-10 opacity-90 shadow-lg',
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {onSelectChange != null && (
          <Checkbox
            checked={selected ?? false}
            onCheckedChange={(c) => onSelectChange(c === true)}
            onClick={(e) => e.stopPropagation()}
            aria-label={`Select ${rule.name}`}
            className="shrink-0"
          />
        )}
        <button
          type="button"
          className={cn(
            'flex shrink-0 cursor-grab touch-none items-center justify-center rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring',
            isDragging && 'cursor-grabbing',
            isReorderPending && 'pointer-events-none opacity-50',
          )}
          aria-label="Drag to reorder"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {rule.color && (
              <span
                className="h-4 w-4 shrink-0 rounded border border-border"
                style={{ backgroundColor: rule.color }}
                title={rule.color}
              />
            )}
            {rule.emoji && (
              <span
                className="shrink-0 text-base leading-none"
                title={`Emoji: ${rule.emoji}`}
                aria-label={`Emoji ${rule.emoji}`}
              >
                {rule.emoji}
              </span>
            )}
            <span className="font-medium">{rule.name}</span>
            {noStats && (
              <span
                className="inline-flex items-center gap-1 rounded bg-primary/10 px-2 py-0.5 text-xs text-primary"
                title="Tags elements the tracker has no stats for yet — only one rule per layer can"
              >
                <Sparkles className="h-3 w-3" aria-hidden />
                No stats
              </span>
            )}
            {!rule.enabled && (
              <span className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">Disabled</span>
            )}
          </div>
          {noStats ? (
            /* Prose, not a blank condition line — and NO date label: a
               no-stats rule has no window, so "Last 30 days" would be a lie. */
            <p className="mt-0.5 text-xs text-muted-foreground">
              Elements with no stats yet — brand new, never served
            </p>
          ) : (
            <>
              <FlowScopedConditionLine
                conditions={rule.conditions}
                scope={scope}
                formatBase={formatFlowCondition}
              />
              <p className="mt-0.5 text-xs text-muted-foreground">{getFlowRuleDateLabel(rule)}</p>
            </>
          )}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <label className="flex cursor-pointer items-center gap-2" title={rule.enabled ? 'Disable' : 'Enable'}>
          <Switch checked={!!rule.enabled} onCheckedChange={onToggleEnabled} disabled={isReorderPending} />
          <span className="sr-only">{rule.enabled ? 'Disable rule' : 'Enable rule'}</span>
        </label>
        <Button variant="ghost" size="icon" onClick={onEdit} className="h-9 w-9" aria-label="Edit rule" title="Edit">
          <Pencil className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={onDelete}
          disabled={isReorderPending}
          className="h-9 w-9 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
          aria-label="Delete rule"
          title="Delete"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
